import { render } from "uhtml";
import { effect } from "alien-signals";

export const updateQueue = {
	queue: new Map(),
	add(element, signals) {
		this.queue.set(element, signals);
		if (!this.renderingQueued && !this.renderingInProgress) {
			// console.log("setting up rendering");
			window.requestAnimationFrame(() => this.render());
			this.renderingQueued = true;
		}
	},
	async renderTask(queue) {
		for (const elementAndSignals of queue) {
			const [element, signals] = elementAndSignals;
			try {
				// console.log("rendering element:", element.tagName);
				await element._internalRender(signals);
				// console.log("finished rendering element: ", element.tagName);
				/* c8 ignore next 3 */
			} catch (err) {
				element._reject(err);
			}
		}
	},
	async render() {
		this.renderingInProgress = true;
		const queue = new Map(this.queue);
		this.queue = new Map();
		await this.renderTask(queue);
		this.renderingInProgress = false;
		this.renderingQueued = false;
		if (this.queue.size !== 0) {
			window.requestAnimationFrame(() => this.render());
		}
	},
};

export function Customised(ElementClass) {
	return class CustomisedElement extends ElementClass {
		_updateCompletePromise = new Promise((res, rej) => {
			this._resolve = res;
			this._reject = rej;
		});
		get updateComplete() {
			if (!this._renderPending) {
				// console.log("fake update promise for: ", this.tagName);
				return Promise.resolve();
			}
			if (!this._updateCompletePromise) {
				this._updateCompletePromise = new Promise((res, rej) => {
					this._resolve = res;
					this._reject = rej;
				});
			}
			return this._updateCompletePromise;
		}
		static define() {
			window.customElements.define(this.tagName, this);
		}
		connectedCallback() {
			this._renderPending = true;
			// only for attribute updates
			this.setupEffects();
			queueMicrotask(() => {
				effect(() => {
					this._renderPending = true;
					const signals = this.signals();
					// throttled to animationframe
					updateQueue.add(this, signals);
				});
			});
		}
		async _internalRender(signals) {
			try {
				const nodes = this.render(signals);
				render(this, nodes);
			} catch (err) {
				console.error(err);
			}
			// console.log("rendering this:", this.tagName);
			this.updateCallback();
		}
		/* c8 ignore next 1 */
		signals() {}
		setupEffects() {}
		updated() {}
		firstUpdated() {}
		updateCallback() {
			// console.log("updateCallback called");
			if (!this._firstUpdate) {
				this.firstUpdated();
				this._firstUpdate = true;
			}
			this.updated();
			this._renderPending = false;
			this._resolve("rendered");
			this._updateCompletePromise = null;
			// console.log("rendered");
		}
	};
}

let currentDialog = null;
export function CustomisedWithDialog(ElementClass) {
	return class CustomisedWithDialogElement extends Customised(ElementClass) {
		connectedCallback() {
			super.connectedCallback();
			this.addEventListener("keydown", (event) => event.stopPropagation());
			this.addEventListener("click", (event) => this.handleClick(event));
		}
		// Need a system that only keeps one dialog open at a time.
		dialog() {
			return this.querySelector("dialog");
		}
		dialogOpen(dialog = this.dialog()) {
			if (currentDialog) currentDialog.close();
			document.addEventListener("click", this);
			if (dialog) {
				currentDialog = dialog;
				dialog.showModal();
				this.setAttribute("open", "open");
				dialog.addEventListener("close", () => this.removeAttribute("open"));
			}
		}
		dialogClose() {
			const dialog = this.dialog();
			if (dialog) {
				dialog.close();
			}
			if (currentDialog) {
				currentDialog.close();
				currentDialog = null;
			}
			document.removeEventListener("click", this);
		}
		handleClick(event) {
			const button = event.target.closest("button");
			if (
				button &&
				(button.dataset.type === "close-dialog" ||
					button.dataset.type === "open-dialog")
			) {
				event.stopPropagation();
				event.preventDefault();
				if (button.dataset.type === "close-dialog") {
					button.blur();
					this.dialogClose();
				} else if (
					button.dataset.type === "open-dialog" &&
					button.dataset.alternateDialog
				) {
					const secondDialog = this.querySelector(
						button.dataset.alternateDialog,
					);
					if (secondDialog) {
						this.dialogOpen(secondDialog);
					}
				} else if (button.dataset.type === "open-dialog") {
					this.dialogOpen();
				}
			}
		}
		handleEvent(event) {
			const target = event.target;
			if (target === null || this.contains(target)) {
				return;
			}
			this.dialogClose();
		}
	};
}
